import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Terminal, Send, CheckCircle2, AlertTriangle, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { n: "01", label: "Read the series" },
  { n: "02", label: "Join the machines" },
  { n: "03", label: "Share the signal" },
];

function CampaignSteps() {
  return (
    <div className="mt-8 grid gap-2 sm:grid-cols-3">
      {steps.map((s) => (
        <div
          key={s.n}
          data-testid={`campaign-step-${s.n}`}
          className="border border-white/10 px-4 py-3 transition-colors duration-200 hover:border-crimson/50"
        >
          <p className="font-display text-sm font-bold text-crimson">{s.n}</p>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[#EAEAEA]/70">
            {s.label}
          </p>
        </div>
      ))}
    </div>
  );
}

function WiredInCounter({ count }) {
  return (
    <div
      data-testid="campaign-count"
      className="mt-4 flex items-center gap-3 border border-white/10 bg-black/40 px-4 py-3"
    >
      <Users className="h-4 w-4 text-neon" />
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-ash">
        Humans wired in
      </span>
      <span className="ml-auto font-display text-lg font-bold text-neon">
        {count === null ? "—" : count}
      </span>
    </div>
  );
}

function SuccessPanel({ message }) {
  return (
    <div
      data-testid="mailing-list-success"
      className="mt-10 flex items-start gap-3 border border-neon/40 bg-neon/5 p-5"
    >
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-neon" />
      <div>
        <p className="font-mono text-sm font-bold uppercase tracking-[0.2em] text-neon">
          Signal received
        </p>
        <p className="mt-1 font-mono text-sm font-light text-[#EAEAEA]/80">{message}</p>
      </div>
    </div>
  );
}

function SignupForm({ email, state, message, onEmailChange, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="mt-10" data-testid="mailing-list-form">
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 border border-white/15 bg-black/40 px-4 focus-within:border-crimson">
          <span className="font-mono text-sm text-crimson">&gt;_</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="your@email.com"
            data-testid="mailing-list-email-input"
            className="w-full bg-transparent py-4 font-mono text-sm text-[#EAEAEA] placeholder:text-ash/50 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={state === "loading"}
          data-testid="mailing-list-submit-button"
          className="inline-flex items-center justify-center gap-2 border border-crimson px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-crimson transition-colors duration-200 hover:bg-crimson hover:text-black hover:shadow-[0_0_30px_rgba(255,46,154,0.4)] disabled:opacity-50"
        >
          {state === "loading" ? "Transmitting..." : "Transmit"}
          <Send className="h-4 w-4" />
        </button>
      </div>
      {state === "error" && (
        <p
          data-testid="mailing-list-error"
          className="mt-4 flex items-center gap-2 font-mono text-sm text-danger"
        >
          <AlertTriangle className="h-4 w-4" />
          {message}
        </p>
      )}
    </form>
  );
}

function subscribeErrorMessage(err) {
  if (err?.response?.status === 409) {
    return "You're already wired in. Instructions will follow soon.";
  }
  return err?.response?.data?.detail || "Transmission failed — try again.";
}

export default function MailingList() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/subscribe/count`)
      .then((res) => setCount(res.data.count))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    setMessage("");
    try {
      const res = await axios.post(`${API}/subscribe`, { email });
      setState("success");
      setMessage(res.data.message || "Signal received. Instructions will follow soon.");
      setEmail("");
      setCount((c) => (c === null ? c : c + 1));
    } catch (err) {
      setState("error");
      setMessage(subscribeErrorMessage(err));
    }
  };

  return (
    <section
      data-testid="mailing-list-section"
      className="relative px-6 pb-32 pt-16 sm:px-12 sm:pt-20 lg:pb-44 lg:pt-20"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-crimson/10 blur-[160px]"
      />
      <div className="mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="terminal-box"
        >
          <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3">
            <span className="h-3 w-3 rounded-full bg-danger/80" />
            <span className="h-3 w-3 rounded-full bg-ember/80" />
            <span className="h-3 w-3 rounded-full bg-neon/80" />
            <span className="ml-3 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ash">
              <Terminal className="h-3.5 w-3.5" />
              comms://join-the-machines
            </span>
          </div>

          <div className="p-8 sm:p-12">
            <p
              data-testid="campaign-badge"
              className="mb-4 inline-block border border-crimson/40 bg-crimson/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-crimson"
            >
              Campaign // Recruitment Drive 0013
            </p>
            <h2
              data-testid="mailing-list-heading"
              className="font-display text-3xl font-bold uppercase tracking-tight sm:text-4xl"
            >
              Join the <span className="text-glow-magenta text-crimson">Machines</span>
            </h2>
            <p className="mt-4 font-mono text-sm font-light leading-relaxed text-ash">
              The machines are recruiting. Plug into the comms list for new chapters, live
              readings, and the road to the web series — instructions will follow soon.
            </p>

            <CampaignSteps />
            <WiredInCounter count={count} />

            {state === "success" ? (
              <SuccessPanel message={message} />
            ) : (
              <SignupForm
                email={email}
                state={state}
                message={message}
                onEmailChange={setEmail}
                onSubmit={submit}
              />
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
